import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import RTPageLayout from '../common/RTPageLayout';
import { apiGet, apiPost } from '../../api/apiClient';
import { toastError, toastSuccess, toastConfirm } from '../common/toast';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';
import { FaPencilAlt } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../common/rtCommon.css';
import './ViewBuildingDetails.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AddRoom from '../rooms/AddRoom';
import RoomList from '../rooms/RoomList';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import {
  OCCUPANCY_TYPE_OPTIONS,
  formatOccupancyType,
  getOccupancyTypeBadgeClassName,
} from '../../utils/occupancyConstants';
import { hasPermission } from '../../utils/permissions';

const StatusPill = ({ status }) => (
  <span
    className={`rt-status-pill rt-status-${status?.toLowerCase() || 'active'}`}
  >
    {status || 'Active'}
  </span>
);

const ViewBuildingDetails = () => {
  const { buildingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [building, setBuilding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState('');
  const [mode, setMode] = useState({
    building: 'view',
    rental: 'view',
    buildingName: 'view',
    addRoom: false,
    editRoom: null,
  });
  const [editBuilding, setEditBuilding] = useState({});
  const [editRental, setEditRental] = useState({});
  const [editBuildingName, setEditBuildingName] = useState({});
  const [updating, setUpdating] = useState(false);
  const [editErrors, setEditErrors] = useState({});
  const [roomsRefresh, setRoomsRefresh] = useState(0);

  useEffect(() => {
    fetchBuilding();
  }, [buildingId]);

  const fetchBuilding = async () => {
    setLoading(true);
    setApiError('');
    try {
      setMode({
        building: 'view',
        rental: 'view',
        buildingName: 'view',
        addRoom: false,
        editRoom: null,
      });
      const response = await apiGet(`/building/view/${buildingId}`);
      if (response && response.data) {
        setBuilding(response.data);
      } else {
        setApiError('Unable to fetch building details.');
      }
    } catch (err) {
      setApiError('Unable to fetch building details.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (section) => {
    setEditErrors({});
    if (section === 'building') {
      setEditBuilding({
        address: building.address || '',
        type: building.type || '',
        occupancy_gender: building.occupancy_gender || '',
        start_date: building.start_date || null,
        end_date: building.end_date || null,
      });
      setMode((prev) => ({ ...prev, building: 'edit' }));
    } else if (section === 'rental') {
      setEditRental({
        owner_name: building.owner_name || '',
        owner_phone: building.owner_phone || '',
        owner_address: building.owner_address || '',
        advance: building.advance != null ? building.advance : null,
        rent: building.rent != null ? building.rent : '',
      });
      setMode((prev) => ({ ...prev, rental: 'edit' }));
    } else if (section === 'buildingName') {
      setEditBuildingName({
        name: building.name || '',
      });
      setMode((prev) => ({ ...prev, buildingName: 'edit' }));
    }
  };

  const handleInputChange = (section, field, value) => {
    if (section === 'building') {
      setEditBuilding((prev) => ({ ...prev, [field]: value }));
    } else if (section === 'rental') {
      setEditRental((prev) => ({ ...prev, [field]: value }));
    } else if (section === 'buildingName') {
      setEditBuildingName((prev) => ({ ...prev, [field]: value }));
    }
  };

  const validateEdit = (section) => {
    const errors = {};
    if (section === 'building') {
      if (!editBuilding.address || editBuilding.address.trim() === '') {
        errors.address = 'Address is required';
      }
      if (!editBuilding.type) {
        errors.type = 'Type is required';
      }
      if (!editBuilding.start_date) {
        errors.start_date = 'Start date is required';
      }
      if (!building.occupancy_gender && !editBuilding.occupancy_gender) {
        errors.occupancy_gender = 'Occupancy gender is required';
      }
    } else if (section === 'rental') {
      if (!editRental.owner_name || editRental.owner_name.trim() === '') {
        errors.owner_name = 'Owner name is required';
      }
      if (!editRental.owner_phone || editRental.owner_phone.trim() === '') {
        errors.owner_phone = 'Owner phone is required';
      }
      if (!editRental.owner_address || editRental.owner_address.trim() === '') {
        errors.owner_address = 'Owner address is required';
      }
      if (
        editRental.advance &&
        (isNaN(Number(editRental.advance)) || Number(editRental.advance) < 0)
      ) {
        errors.advance = 'Advance must be a positive number';
      }
      // Make Rent mandatory and validate
      if (
        editRental.rent === '' ||
        editRental.rent === null ||
        isNaN(Number(editRental.rent)) ||
        Number(editRental.rent) <= 0
      ) {
        errors.rent = 'Rent is required and must be a positive number';
      }
    } else if (section === 'buildingName') {
      if (!editBuildingName.name || editBuildingName.name.trim() === '') {
        errors.name = 'Building name is required';
      }
    }
    return errors;
  };

  const handleVacateBuilding = async (id) => {
    const confirmVacate = await toastConfirm(
      true,
      'Are you sure you want to vacate this building?',
    );
    if (confirmVacate) {
      try {
        const response = await apiPost(`/building/vacate/${id}`);
        if (response && response.data && response.data.status === 'success') {
          toastSuccess('Vacated Building Successfully');
          fetchBuilding();
        } else {
          toastError('Failed to vacate building. Please try again.');
        }
      } catch (err) {
        toastError(err.response?.data?.detail || 'Failed to vacate building.');
      }
    }
  };

  const handleSave = async (section) => {
    const errors = validateEdit(section);
    setEditErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setUpdating(true);
    let data = {};
    if (section === 'building') {
      data = {
        ...building,
        ...editBuilding,
        start_date: editBuilding.start_date,
        end_date: editBuilding.end_date,
      };
    } else if (section === 'rental') {
      data = {
        ...building,
        ...editRental,
        start_date: building.start_date,
        end_date: building.end_date,
        advance: editRental.advance === '' ? null : Number(editRental.advance),
        rent: editRental.rent === '' ? null : Number(editRental.rent),
      };
    } else if (section === 'buildingName') {
      data = {
        ...building,
        name: editBuildingName.name,
      };
    }
    try {
      const response = await apiPost('/building', data);
      if (response && response.data && response.data.building_id) {
        toastSuccess('Building details updated successfully!');
        setMode((prev) => ({
          ...prev,
          [section]: 'view',
          buildingName: section === 'buildingName' ? 'view' : prev.buildingName,
        }));
        fetchBuilding();
        if (section === 'building') {
          setRoomsRefresh((r) => r + 1);
        }
      } else {
        toastError('Failed to update building. Please try again.');
      }
    } catch (err) {
      toastError('Failed to update building. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleAddRoom = () => {
    setMode((prev) => ({ ...prev, addRoom: true }));
  };

  return (
    <RTPageLayout
      title="Building Details"
      actions={
        <>
          <RTButton variant="secondary" onClick={() => navigate(-2)}>
            &#8592; Back
          </RTButton>
          {building && building.status === 'Active' && (
            <RTButton
              variant="secondary"
              permissionKey="building.add"
              onClick={() => handleVacateBuilding(building.id)}
            >
              Vacate Building
            </RTButton>
          )}
        </>
      }
      mainSection={true}
      activePath={location.pathname}
    >
      {loading || updating ? (
        <div className="rt-center-content">
          <RTSpinner
            message={
              updating ? 'Updating building details...' : 'Loading building details...'
            }
          />
        </div>
      ) : apiError ? (
        <div className="rt-error-message">{apiError}</div>
      ) : building ? (
        <>
          <div className="rt-details-header">
            <div
              className="rt-details-title"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                width: '100%',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {mode.buildingName === 'edit' ? (
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <span>🏢</span>
                    <input
                      type="text"
                      value={editBuildingName.name}
                      onChange={(e) =>
                        handleInputChange(
                          'buildingName',
                          'name',
                          e.target.value,
                        )
                      }
                      className={`rt-input${editErrors.name ? ' rt-input-error' : ''}`}
                      style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        minWidth: '200px',
                      }}
                      autoFocus
                    />
                    <RTButton
                      onClick={() => handleSave('buildingName')}
                      variant="primary"
                      disabled={updating}
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                    >
                      Save
                    </RTButton>
                    <RTButton
                      onClick={() => {
                        setMode((prev) => ({ ...prev, buildingName: 'view' }));
                        setEditBuildingName({ name: building.name || '' });
                        setEditErrors({});
                      }}
                      variant="secondary"
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                    >
                      Cancel
                    </RTButton>
                    {editErrors.name && (
                      <div
                        className="rt-field-error"
                        style={{ fontSize: '12px' }}
                      >
                        {editErrors.name}
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <span className="rt-details-building-name">
                      {'🏢 ' + building.name}
                    </span>
                    {building.occupancy_gender && (
                      <span
                        className={getOccupancyTypeBadgeClassName(
                          building.occupancy_gender,
                        )}
                      >
                        {formatOccupancyType(building.occupancy_gender)}
                      </span>
                    )}
                    {building.status === 'Active' &&
                      hasPermission('building.add') && (
                      <FaPencilAlt
                        className="rt-edit-icon"
                        onClick={() => handleEditClick('buildingName')}
                        style={{
                          cursor: 'pointer',
                          fontSize: '14px',
                        }}
                        title="Edit building name"
                      />
                    )}
                  </div>
                )}
              </div>
              <StatusPill status={building.status} />
              <div style={{ flex: 1 }} />
            </div>
          </div>
          <Tabs
            defaultActiveKey="details"
            id="building-details-tabs"
            className="rt-fullwidth-tabs"
            justify
          >
            <Tab
              eventKey="details"
              title="Details"
              style={{ minHeight: '60%', border: '0.5px solid #e0e0e0' }}
            >
              <div
                className="rt-details-tabs-row"
                style={{
                  display: 'flex',
                  gap: 32,
                  marginTop: 18,
                  flexWrap: 'wrap',
                  minHeight: '500px',
                }}
              >
                {/* Building Details Section */}
                <div className="rt-details-col">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                    }}
                  >
                    <h4 className="rt-details-section-title">
                      Building Details
                    </h4>
                    {mode.building === 'view' &&
                      building.status !== 'Vacated' &&
                      hasPermission('building.add') && (
                        <FaPencilAlt
                          className="rt-edit-icon"
                          onClick={() => handleEditClick('building')}
                          style={{
                            cursor: 'pointer',
                            marginLeft: '15px',
                            marginBottom: '15px',
                          }}
                          title="Edit"
                        />
                      )}
                  </div>
                  <div className="rt-details-form-table">
                    {mode.building === 'edit' ? (
                      <>
                        <div className="rt-details-form-row">
                          <div className="rt-details-form-label">
                            Address <span style={{ color: '#d32f2f' }}>*</span>
                          </div>
                          <div className="rt-details-form-value">
                            <input
                              type="text"
                              value={editBuilding.address}
                              onChange={(e) =>
                                handleInputChange(
                                  'building',
                                  'address',
                                  e.target.value,
                                )
                              }
                              className={`rt-input${editErrors.address ? ' rt-input-error' : ''}`}
                            />
                            {editErrors.address && (
                              <div className="rt-field-error">
                                {editErrors.address}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="rt-details-form-row">
                          <div className="rt-details-form-label">
                            Type <span style={{ color: '#d32f2f' }}>*</span>
                          </div>
                          <div className="rt-details-form-value">
                            <select
                              value={editBuilding.type}
                              onChange={(e) =>
                                handleInputChange(
                                  'building',
                                  'type',
                                  e.target.value,
                                )
                              }
                              className={`rt-input${editErrors.type ? ' rt-input-error' : ''}`}
                            >
                              <option value="">Select</option>
                              <option value="own">Own</option>
                              <option value="rental">Rental</option>
                            </select>
                            {editErrors.type && (
                              <div className="rt-field-error">
                                {editErrors.type}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="rt-details-form-row">
                          <div className="rt-details-form-label">
                            Occupancy Gender{' '}
                            {!building.occupancy_gender && (
                              <span style={{ color: '#d32f2f' }}>*</span>
                            )}
                          </div>
                          <div className="rt-details-form-value">
                            <select
                              value={editBuilding.occupancy_gender}
                              onChange={(e) =>
                                handleInputChange(
                                  'building',
                                  'occupancy_gender',
                                  e.target.value,
                                )
                              }
                              className={`rt-input${editErrors.occupancy_gender ? ' rt-input-error' : ''}`}
                            >
                              <option value="">Select</option>
                              {OCCUPANCY_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {editErrors.occupancy_gender && (
                              <div className="rt-field-error">
                                {editErrors.occupancy_gender}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="rt-details-form-row">
                          <div className="rt-details-form-label">
                            Start Date{' '}
                            <span style={{ color: '#d32f2f' }}>*</span>
                          </div>
                          <div className="rt-details-form-value">
                            <DatePicker
                              selected={
                                editBuilding.start_date
                                  ? new Date(editBuilding.start_date)
                                  : null
                              }
                              onChange={(date) =>
                                handleInputChange(
                                  'building',
                                  'start_date',
                                  date ? date.toISOString().split('T')[0] : '',
                                )
                              }
                              dateFormat="yyyy-MM-dd"
                              className={`rt-input${editErrors.start_date ? ' rt-input-error' : ''}`}
                              placeholderText="Select start date"
                            />
                            {editErrors.start_date && (
                              <div className="rt-field-error">
                                {editErrors.start_date}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="rt-details-form-row">
                          <div className="rt-details-form-label">End Date</div>
                          <div className="rt-details-form-value">
                            <DatePicker
                              selected={
                                editBuilding.end_date
                                  ? new Date(editBuilding.end_date)
                                  : null
                              }
                              onChange={(date) =>
                                handleInputChange(
                                  'building',
                                  'end_date',
                                  date ? date.toISOString().split('T')[0] : '',
                                )
                              }
                              dateFormat="yyyy-MM-dd"
                              className="rt-input"
                              placeholderText="Select end date"
                              isClearable
                            />
                          </div>
                        </div>
                        <div
                          style={{
                            textAlign: 'right',
                            marginTop: 16,
                            display: 'flex',
                            gap: 10,
                            justifyContent: 'flex-end',
                          }}
                        >
                          <RTButton
                            variant="secondary"
                            onClick={() =>
                              setMode((prev) => ({ ...prev, building: 'view' }))
                            }
                          >
                            Cancel
                          </RTButton>
                          <RTButton
                            variant="primary"
                            onClick={() => handleSave('building')}
                          >
                            Save
                          </RTButton>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="rt-details-form-row">
                          <div className="rt-details-form-label">Address</div>
                          <div className="rt-details-form-value">
                            {building.address || '-'}
                          </div>
                        </div>
                        <div className="rt-details-form-row">
                          <div className="rt-details-form-label">Type</div>
                          <div className="rt-details-form-value">
                            {building.type
                              ? building.type.charAt(0).toUpperCase() +
                                building.type.slice(1)
                              : '-'}
                          </div>
                        </div>
                        <div className="rt-details-form-row">
                          <div className="rt-details-form-label">
                            Occupancy Gender
                          </div>
                          <div className="rt-details-form-value">
                            {formatOccupancyType(building.occupancy_gender)}
                          </div>
                        </div>
                        <div className="rt-details-form-row">
                          <div className="rt-details-form-label">
                            Start Date
                          </div>
                          <div className="rt-details-form-value">
                            {formatDateToDDMMYYYY(building.start_date)}
                          </div>
                        </div>
                        <div className="rt-details-form-row">
                          <div className="rt-details-form-label">End Date</div>
                          <div className="rt-details-form-value">
                            {formatDateToDDMMYYYY(building.end_date)}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {/* Rental Details Section - Only show if building type is rental */}
                {building.type === 'rental' && (
                  <div className="rt-details-col">
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'left',
                        alignItems: 'center',
                      }}
                    >
                      <h4 className="rt-details-section-title">
                        Rental Details
                      </h4>
                      {mode.rental === 'view' &&
                        building.status !== 'Vacated' &&
                        hasPermission('building.add') && (
                          <FaPencilAlt
                            className="rt-edit-icon"
                            onClick={() => handleEditClick('rental')}
                            style={{
                              cursor: 'pointer',
                              marginLeft: '15px',
                              marginBottom: '15px',
                            }}
                            title="Edit"
                          />
                        )}
                    </div>
                    <div className="rt-details-form-table">
                      {mode.rental === 'edit' ? (
                        <>
                          <div className="rt-details-form-row">
                            <div className="rt-details-form-label">
                              Owner Name{' '}
                              <span style={{ color: '#d32f2f' }}>*</span>
                            </div>
                            <div className="rt-details-form-value">
                              <input
                                type="text"
                                value={editRental.owner_name}
                                onChange={(e) =>
                                  handleInputChange(
                                    'rental',
                                    'owner_name',
                                    e.target.value,
                                  )
                                }
                                className={`rt-input${editErrors.owner_name ? ' rt-input-error' : ''}`}
                              />
                              {editErrors.owner_name && (
                                <div className="rt-field-error">
                                  {editErrors.owner_name}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="rt-details-form-row">
                            <div className="rt-details-form-label">
                              Owner Phone{' '}
                              <span style={{ color: '#d32f2f' }}>*</span>
                            </div>
                            <div className="rt-details-form-value">
                              <input
                                type="text"
                                value={editRental.owner_phone}
                                onChange={(e) =>
                                  handleInputChange(
                                    'rental',
                                    'owner_phone',
                                    e.target.value,
                                  )
                                }
                                className={`rt-input${editErrors.owner_phone ? ' rt-input-error' : ''}`}
                              />
                              {editErrors.owner_phone && (
                                <div className="rt-field-error">
                                  {editErrors.owner_phone}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="rt-details-form-row">
                            <div className="rt-details-form-label">
                              Owner Address{' '}
                              <span style={{ color: '#d32f2f' }}>*</span>
                            </div>
                            <div className="rt-details-form-value">
                              <input
                                type="text"
                                value={editRental.owner_address}
                                onChange={(e) =>
                                  handleInputChange(
                                    'rental',
                                    'owner_address',
                                    e.target.value,
                                  )
                                }
                                className={`rt-input${editErrors.owner_address ? ' rt-input-error' : ''}`}
                              />
                              {editErrors.owner_address && (
                                <div className="rt-field-error">
                                  {editErrors.owner_address}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="rt-details-form-row">
                            <div className="rt-details-form-label">Advance</div>
                            <div className="rt-details-form-value">
                              <input
                                type="number"
                                value={editRental.advance}
                                onChange={(e) =>
                                  handleInputChange(
                                    'rental',
                                    'advance',
                                    e.target.value,
                                  )
                                }
                                className="rt-input"
                              />
                              {editErrors.advance && (
                                <div
                                  className="rt-error-message"
                                  style={{ fontSize: 12, marginTop: 2 }}
                                >
                                  {editErrors.advance}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="rt-details-form-row">
                            <div className="rt-details-form-label">
                              Rent <span style={{ color: '#d32f2f' }}>*</span>
                            </div>
                            <div className="rt-details-form-value">
                              <input
                                type="number"
                                value={editRental.rent}
                                onChange={(e) =>
                                  handleInputChange(
                                    'rental',
                                    'rent',
                                    e.target.value,
                                  )
                                }
                                className={`rt-input${editErrors.rent ? ' rt-input-error' : ''}`}
                              />
                              {editErrors.rent && (
                                <div className="rt-field-error">
                                  {editErrors.rent}
                                </div>
                              )}
                            </div>
                          </div>
                          <div
                            style={{
                              textAlign: 'right',
                              marginTop: 16,
                              display: 'flex',
                              gap: 10,
                              justifyContent: 'flex-end',
                            }}
                          >
                            <RTButton
                              variant="secondary"
                              onClick={() =>
                                setMode((prev) => ({ ...prev, rental: 'view' }))
                              }
                            >
                              Cancel
                            </RTButton>
                            <RTButton
                              variant="primary"
                              onClick={() => handleSave('rental')}
                            >
                              Save
                            </RTButton>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="rt-details-form-row">
                            <div className="rt-details-form-label">
                              Owner Name
                            </div>
                            <div className="rt-details-form-value">
                              {building.owner_name || '-'}
                            </div>
                          </div>
                          <div className="rt-details-form-row">
                            <div className="rt-details-form-label">
                              Owner Phone
                            </div>
                            <div className="rt-details-form-value">
                              {building.owner_phone || '-'}
                            </div>
                          </div>
                          <div className="rt-details-form-row">
                            <div className="rt-details-form-label">
                              Owner Address
                            </div>
                            <div className="rt-details-form-value">
                              {building.owner_address || '-'}
                            </div>
                          </div>
                          <div className="rt-details-form-row">
                            <div className="rt-details-form-label">Advance</div>
                            <div className="rt-details-form-value">
                              {building.advance != null
                                ? `₹ ${building.advance}`
                                : '-'}
                            </div>
                          </div>
                          <div className="rt-details-form-row">
                            <div className="rt-details-form-label">Rent</div>
                            <div className="rt-details-form-value">
                              {building.rent != null
                                ? `₹ ${building.rent}`
                                : '-'}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Tab>
            <Tab
              eventKey="rooms"
              title="Rooms"
              style={{ border: '.5px solid #e0e0e0' }}
            >
              {mode.addRoom ? (
                <div
                  style={{
                    marginTop: '0px', // Remove top margin
                    paddingTop: '0px', // Remove top padding
                    display: 'flex',
                    justifyContent: 'center',
                    minHeight: 'calc(100vh - 200px)', // Adjust height to account for header and tabs
                  }}
                >
                  <AddRoom
                    buildingId={building.id}
                    buildingOccupancyGender={building.occupancy_gender}
                    room={mode.editRoom}
                    onSuccess={() => {
                      setMode((prev) => ({
                        ...prev,
                        addRoom: false,
                        editRoom: null,
                      }));
                      setRoomsRefresh((r) => r + 1); // Refresh the room list
                    }}
                    onCancel={() =>
                      setMode((prev) => ({
                        ...prev,
                        addRoom: false,
                        editRoom: null,
                      }))
                    }
                  />
                </div>
              ) : (
                <RoomList
                  buildingId={building.id}
                  buildingName={building.name}
                  buildingStatus={building.status}
                  onEditRoom={(room) =>
                    setMode((prev) => ({
                      ...prev,
                      addRoom: true,
                      editRoom: room,
                    }))
                  }
                  onAddRoom={handleAddRoom}
                  refresh={roomsRefresh}
                />
              )}
            </Tab>
          </Tabs>
        </>
      ) : null}
    </RTPageLayout>
  );
};

export default ViewBuildingDetails;
